import type { ChimeAttendeeBackend, ChimeMeetingBackend, ChimeAttendee, ChimeMeeting } from '@models/meeting-models'

/**
 * Converts snake_case property names to PascalCase for Amazon Chime SDK compatibility.
 *
 * The backend returns Chime data in snake_case (e.g., audio_host_url, media_placement),
 * but the Chime SDK expects PascalCase (e.g., AudioHostUrl, MediaPlacement).
 *
 * @param obj - The object with snake_case properties
 * @returns A new object with PascalCase properties
 */
export function snakeToPascalCase(obj: unknown): unknown {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => snakeToPascalCase(item));
  }

  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj)) {
      const pascalKey = key
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join('');

      result[pascalKey] = snakeToPascalCase(value);
    }

    return result;
  }

  return obj;
}

/**
 * Transforms backend Chime meeting and attendee data to the format expected by the Chime SDK.
 *
 * @param meetingData - Raw meeting data from backend (snake_case)
 * @param attendeeData - Raw attendee data from backend (snake_case)
 * @returns Transformed data in PascalCase format for Chime SDK
 */
export function transformChimeData(
  meetingData: ChimeMeetingBackend | Record<string, unknown>,
  attendeeData: ChimeAttendeeBackend | Record<string, unknown>
): {
  meeting: ChimeMeeting
  attendee: ChimeAttendee
} {
  return {
    meeting: snakeToPascalCase(meetingData) as ChimeMeeting,
    attendee: snakeToPascalCase(attendeeData) as ChimeAttendee,
  };
}
